// src/pages/super-admin/SuperAdminSchools.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  AcademicCapIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  EllipsisVerticalIcon,
  EnvelopeIcon,
  ListBulletIcon,
  MagnifyingGlassIcon,
  PhoneIcon,
  Squares2X2Icon,
  UserGroupIcon,
  XCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import apiClient from "../../api/client";
import { FireLoader } from "../../components/Loader";
import { showConfirm, showError, showSuccess } from "../../utils/alert";

const number = (value) => new Intl.NumberFormat().format(Number(value || 0));
const initials = (name) => String(name || "School").split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word[0]).join("").toUpperCase();

const schoolName = (school) =>
  school?.school_name || school?.schoolName || school?.name || "School";

const resolveSchoolImageUrl = (school) => {
  const candidate =
    school?.logo_url ||
    school?.logoUrl ||
    school?.logo ||
    school?.logo_path ||
    school?.school_logo ||
    school?.badge_url ||
    school?.badgeUrl ||
    school?.badge ||
    school?.badge_path ||
    school?.school_badge ||
    school?.image_url ||
    school?.image ||
    school?.profile?.logo_url ||
    school?.profile?.logo ||
    school?.profile?.badge_url ||
    school?.profile?.badge;

  const rawValue =
    typeof candidate === "object"
      ? candidate?.url || candidate?.path || candidate?.location
      : candidate;

  if (!rawValue) return "";

  const value = String(rawValue).trim().replace(/\\/g, "/");
  if (!value) return "";
  if (/^(https?:|data:|blob:)/i.test(value)) return value;

  const baseUrl =
    apiClient?.defaults?.baseURL || process.env.REACT_APP_API_URL || "";

  try {
    const apiOrigin = new URL(baseUrl || window.location.origin, window.location.origin).origin;
    if (value.startsWith("/")) return `${apiOrigin}${value}`;
    if (value.includes("/")) return `${apiOrigin}/${value.replace(/^\/+/, "")}`;
    return `${apiOrigin}/uploads/schools/${value}`;
  } catch {
    return value.startsWith("/") ? value : `/${value}`;
  }
};

const SchoolImage = ({ school, className }) => {
  const imageUrl = resolveSchoolImageUrl(school);
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [imageUrl]);

  if (!imageUrl || imageFailed) {
    return (
      <span className={`${className} flex items-center justify-center bg-primary-50 font-bold text-primary-700`}>
        {initials(schoolName(school))}
      </span>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={`${schoolName(school)} logo`}
      className={`${className} bg-white object-contain p-1`}
      loading="lazy"
      onError={() => setImageFailed(true)}
    />
  );
};

export default function SuperAdminSchools() {
  const [schools, setSchools] = useState([]);
  const [summary, setSummary] = useState({
    total_schools: 0,
    active_schools: 0,
    total_students: 0,
    total_teachers: 0,
  });
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("superAdminSchoolsViewMode") || "grid");
  const menuRef = useRef(null);

  useEffect(() => { const timer = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 350); return () => clearTimeout(timer); }, [searchInput]);
  useEffect(() => { localStorage.setItem("superAdminSchoolsViewMode", viewMode); }, [viewMode]);
  useEffect(() => {
    const close = (event) => { if (menuRef.current && !menuRef.current.contains(event.target)) setActiveMenuId(null); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const response = await apiClient.get("/super-admin/dashboard", { params: { search, status, page, limit: 12 } });
      const payload = response?.data?.data || {};
      setSchools(Array.isArray(payload.schools) ? payload.schools : []);
      setSummary((current) => ({ ...current, ...(payload.summary || {}) }));
      setPagination(payload.pagination || { page: 1, pages: 1, total: 0 });
    } catch (error) {
      await showError({ title: "Unable to Load Schools", text: error?.response?.data?.message || "School profiles could not be loaded." });
    } finally { setLoading(false); }
  }, [search, status, page]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (school) => {
    setActiveMenuId(null);
    const nextStatus = school.status === "active" ? "inactive" : "active";
    const confirmed = await showConfirm({
      title: nextStatus === "active" ? "Activate School" : "Deactivate School",
      text: nextStatus === "active" ? `Allow ${school.school_name} to use the platform?` : `Deactivate ${school.school_name}? School users will lose access.`,
      confirmText: nextStatus === "active" ? "Yes, activate" : "Yes, deactivate",
      icon: nextStatus === "active" ? "question" : "warning",
    });
    if (!confirmed) return;
    try {
      await apiClient.patch(`/super-admin/schools/${school.id}/status`, { status: nextStatus });
      await showSuccess({ title: "School Updated", text: `${school.school_name} is now ${nextStatus}.` });
      load(true);
    } catch (error) { await showError({ title: "Update Failed", text: error?.response?.data?.message || "School status could not be changed." }); }
  };

  const Menu = ({ school }) => (
    <div className="relative" ref={activeMenuId === school.id ? menuRef : null}>
      <button onClick={() => setActiveMenuId((id) => id === school.id ? null : school.id)} className="flex h-9 w-9 items-center justify-center rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-700"><EllipsisVerticalIcon className="h-5 w-5" /></button>
      {activeMenuId === school.id && (
        <div className="absolute right-0 top-10 z-30 w-52 rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl">
          <button onClick={() => updateStatus(school)} className={`flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold ${school.status === "active" ? "text-red-600 hover:bg-red-50" : "text-emerald-700 hover:bg-emerald-50"}`}>
            {school.status === "active" ? <XCircleIcon className="h-4 w-4" /> : <CheckCircleIcon className="h-4 w-4" />}
            {school.status === "active" ? "Deactivate school" : "Activate school"}
          </button>
        </div>
      )}
    </div>
  );

  if (loading) return <FireLoader fullScreen size="lg" text="Loading..." />;

  const cards = [
    {
      label: "Registered schools",
      value: summary.total_schools,
      note: "All platform schools",
      icon: BuildingOffice2Icon,
      style: "bg-blue-50 text-blue-600",
    },
    {
      label: "Active schools",
      value: summary.active_schools,
      note: "Currently active",
      icon: CheckCircleIcon,
      style: "bg-emerald-50 text-emerald-600",
    },
    {
      label: "Students on platform",
      value: summary.total_students,
      note: "Across all schools",
      icon: UserGroupIcon,
      style: "bg-violet-50 text-violet-600",
    },
    {
      label: "Teachers on platform",
      value: summary.total_teachers,
      note: "Across all schools",
      icon: AcademicCapIcon,
      style: "bg-amber-50 text-amber-600",
    },
  ];

  return (
    <div className="space-y-5">
      <Helmet><title>Schools | PawaSchool Super Admin</title></Helmet>
      <div><h1 className="text-2xl font-bold text-slate-900">Schools</h1><p className="mt-1 text-sm text-slate-400">Manage every registered school from one place.</p></div>

      <section className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {cards.map(({ label, value, note, icon: Icon, style }) => (
          <article
            key={label}
            className="rounded-xl border border-slate-200 bg-white p-3 transition-colors hover:border-primary-200 sm:p-5"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-[10px] font-semibold uppercase tracking-wider text-slate-400 sm:text-xs">
                  {label}
                </p>
                <p className="mt-1 text-xl font-bold text-slate-800 sm:text-3xl">
                  {number(value)}
                </p>
              </div>
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl sm:h-12 sm:w-12 ${style}`}>
                <Icon className="h-5 w-5 sm:h-6 sm:w-6" />
              </span>
            </div>
            <p className="mt-3 border-t border-slate-100 pt-3 text-[10px] text-slate-400 sm:text-xs">
              {note}
            </p>
          </article>
        ))}
      </section>

      <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
        <div className="flex flex-wrap items-center gap-3 lg:flex-nowrap">
          <div className="min-w-[150px] flex-1 lg:max-w-[220px]"><p className="font-bold text-slate-800">School profiles <span className="ml-1 rounded-full bg-primary-50 px-2 py-1 text-xs text-primary-700">{number(pagination.total)}</span></p><p className="text-xs text-slate-400">Platform school records</p></div>
          <label className="relative min-w-[210px] flex-[2_1_280px] lg:max-w-md"><MagnifyingGlassIcon className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><input value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search schools..." className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-4 text-sm outline-none focus:border-primary-400 focus:bg-white focus:ring-4 focus:ring-primary-100" /></label>
          <select value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-primary-400 focus:ring-4 focus:ring-primary-100"><option value="all">All schools</option><option value="active">Active</option><option value="inactive">Inactive</option></select>
          <div className="ml-auto inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1">
            <button onClick={() => setViewMode("grid")} className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium ${viewMode === "grid" ? "bg-white text-primary-700 shadow-sm" : "text-slate-500"}`}><Squares2X2Icon className="h-4 w-4" /><span className="hidden xl:inline">Cards</span></button>
            <button onClick={() => setViewMode("list")} className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium ${viewMode === "list" ? "bg-white text-primary-700 shadow-sm" : "text-slate-500"}`}><ListBulletIcon className="h-4 w-4" /><span className="hidden xl:inline">List</span></button>
          </div>
        </div>
      </div>

      {!schools.length ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-14 text-center"><BuildingOffice2Icon className="mx-auto h-10 w-10 text-slate-300" /><p className="mt-3 font-bold text-slate-700">No schools found</p></div>
      ) : viewMode === "grid" ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {schools.map((school) => (
            <article
              key={school.id}
              className="relative overflow-visible rounded-3xl border border-slate-200/80 bg-gradient-to-b from-white to-slate-50/70 p-5 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-primary-200 hover:shadow-xl hover:shadow-slate-200/60"
            >
              <div className="absolute right-3 top-3 z-10">
                <Menu school={school} />
              </div>

              <div className="flex flex-col items-center px-3 pt-2 text-center">
                <SchoolImage
                  school={school}
                  className="h-24 w-24 rounded-2xl border-4 border-white shadow-lg shadow-slate-200/70 ring-1 ring-slate-200"
                />
                <h3 className="mt-4 flex min-h-[3.25rem] w-full items-center justify-center text-lg font-extrabold leading-tight tracking-tight text-slate-900">
                  {schoolName(school)}
                </h3>
                <p className="mt-1 text-xs font-medium text-slate-400">
                  {school.school_code || `School #${school.id}`}
                </p>

                <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                  <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${school.status === "active" ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100" : "bg-slate-100 text-slate-500 ring-1 ring-slate-200"}`}>
                    {school.status}
                  </span>
                  <span className="rounded-full bg-primary-50 px-3 py-1 text-[10px] font-bold text-primary-700 ring-1 ring-primary-100">
                    {school.plan_name || "No plan"}
                  </span>
                </div>
              </div>

              <div className="mt-5 grid grid-cols-3 divide-x divide-slate-200 rounded-2xl border border-slate-100 bg-white/90 py-3.5 text-center shadow-sm">
                <div>
                  <b className="text-base text-slate-800">{number(school.student_count)}</b>
                  <p className="mt-0.5 text-[10px] font-medium text-slate-400">Students</p>
                </div>
                <div>
                  <b className="text-base text-slate-800">{number(school.teacher_count)}</b>
                  <p className="mt-0.5 text-[10px] font-medium text-slate-400">Teachers</p>
                </div>
                <div>
                  <b className="text-base text-slate-800">{number(school.admin_count)}</b>
                  <p className="mt-0.5 text-[10px] font-medium text-slate-400">Admins</p>
                </div>
              </div>

              <div className="mt-4 space-y-2.5 rounded-2xl bg-slate-100/70 p-3 text-xs text-slate-500">
                <p className="flex items-center gap-2.5 truncate">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white text-primary-600 shadow-sm">
                    <EnvelopeIcon className="h-3.5 w-3.5" />
                  </span>
                  <span className="truncate">{school.email || "No email"}</span>
                </p>
                <p className="flex items-center gap-2.5 truncate">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white text-primary-600 shadow-sm">
                    <PhoneIcon className="h-3.5 w-3.5" />
                  </span>
                  <span className="truncate">{school.phone || "No phone"}</span>
                </p>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="space-y-3">{schools.map((school) => <article key={school.id} className="rounded-2xl border border-slate-200 bg-white p-4 hover:border-primary-200 hover:shadow-md"><div className="flex items-center gap-3"><SchoolImage school={school} className="h-11 w-11 shrink-0 rounded-xl border border-slate-200" /><div className="min-w-0 flex-1"><p className="truncate font-bold text-slate-900">{schoolName(school)}</p><p className="truncate text-xs text-slate-400">{school.school_code || `School #${school.id}`} {school.email ? `· ${school.email}` : ""}</p></div><div className="hidden text-center sm:block"><p className="text-xs text-slate-400">Students</p><b className="text-sm">{number(school.student_count)}</b></div><div className="hidden text-center md:block"><p className="text-xs text-slate-400">Teachers</p><b className="text-sm">{number(school.teacher_count)}</b></div><span className={`hidden rounded-full px-2.5 py-1 text-[10px] font-bold uppercase lg:inline ${school.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{school.status}</span><Menu school={school} /></div></article>)}</div>
      )}

      {pagination.pages > 1 && <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3"><p className="text-xs text-slate-400">Page {pagination.page} of {pagination.pages}</p><div className="flex gap-2"><button disabled={page <= 1} onClick={() => setPage((value) => value - 1)} className="rounded-lg border p-2 disabled:opacity-40"><ChevronLeftIcon className="h-4 w-4" /></button><button disabled={page >= pagination.pages} onClick={() => setPage((value) => value + 1)} className="rounded-lg border p-2 disabled:opacity-40"><ChevronRightIcon className="h-4 w-4" /></button></div></div>}
    </div>
  );
}